import React, {useState} from 'react';
import {ConditionalAcceptanceRequest} from "../api/ApiTypes.ts";
import SamButton from "./SamButton.tsx";
import {Textarea} from "@headlessui/react";
import {useOrderApi} from "../context/ApiContext.tsx";
import CancelButton from "./CancelButton.tsx";

interface RejectWithConditionsProps {
    orderId: number,
    onSuccess: () => void;
    onCancel: () => void;
}

export const RejectWithConditionsForm : React.FC<RejectWithConditionsProps> = ({
    orderId,
    onSuccess,
    onCancel
}) => {

    const defaultState: ConditionalAcceptanceRequest = {
        orderId: orderId,
        forAppraiser: undefined,
        dueDate: '',
        fee: '',
        standardFee: false,
        complexity: false,
        distance: false,
        other: false,
        acceptConditions: ''
    };

    const [ formData, setFormData ] = useState<ConditionalAcceptanceRequest>(defaultState);
    const orderApi = useOrderApi();

    const handleSubmit = (e : React.FormEvent) => {
        e.preventDefault();

        orderApi.rejectWithConditions(formData).then(_ => {
            onSuccess();
        }).catch(console.log);
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {

        const { name, value, type } = e.target;

        // Type guard to handle checkbox inputs
        const inputValue = type === 'checkbox'
            ? (e.target as HTMLInputElement).checked
            : value;

        setFormData(prevState => ({
            ...prevState,
            [name]: inputValue
        }));
    };

    const handleCancel = () => {
        setFormData(defaultState);
        onCancel();
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                    <label htmlFor="condDueDate">Due Date</label>
                    <div className="relative">
                        <input
                            type="date"
                            name="dueDate"
                            id="condDueDate"
                            value={formData.dueDate}
                            onChange={handleInputChange}
                            className="w-full p-2 border-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <label htmlFor="condFee">Fee</label>
                    <input
                        type="text"
                        name="fee"
                        id="condFee"
                        value={formData.fee}
                        onChange={handleInputChange}
                        pattern="[0-9.]+"
                        className="w-full p-2 border-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="condStandardFee"
                        name="standardFee"
                        checked={formData.standardFee}
                        onChange={handleInputChange}
                        className="group block size-4 rounded border bg-white data-[checked]:bg-blue-500"
                    />
                    <label htmlFor="condStandardFee"
                           className="text-sm cursor-pointer select-none"
                    >
                        Standard Fee
                    </label>
                </div>

                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="condComplexity"
                        name="complexity"
                        checked={formData.complexity}
                        onChange={handleInputChange}
                        className="group block size-4 rounded border bg-white data-[checked]:bg-blue-500"
                    />
                    <label htmlFor="condComplexity"
                           className="text-sm cursor-pointer select-none"
                    >
                        Complexity
                    </label>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="condDistance"
                        name="distance"
                        checked={formData.distance}
                        onChange={handleInputChange}
                        className="group block size-4 rounded border bg-white data-[checked]:bg-blue-500"
                    />
                    <label htmlFor="condDistance"
                           className="text-sm cursor-pointer select-none"
                    >
                        Distance
                    </label>
                </div>

                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="condOther"
                        name="other"
                        checked={formData.other}
                        onChange={handleInputChange}
                        className="group block size-4 rounded border bg-white data-[checked]:bg-blue-500"
                    />
                    <label htmlFor="condOther"
                           className="text-sm cursor-pointer select-none"
                    >
                        Other
                    </label>
                </div>
            </div>

            <div className="space-y-2">
                <label htmlFor="acceptConditions">Comments</label>
                <Textarea
                    id="acceptConditions"
                    name="acceptConditions"
                    value={formData.acceptConditions}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
            </div>

            <div className="flex justify-end space-x-2">
                <CancelButton
                    type="button"
                    onClick={handleCancel}
                >
                    Cancel
                </CancelButton>
                <SamButton
                    type="submit"
                >
                    Reject with Conditions
                </SamButton>
            </div>
        </form>
    );
}